import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_app/schema-builder/")({
  component: SchemaBuilderPage,
});

import { useState } from "react";
import { Button } from "@papier/ui/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@papier/ui/components/ui/card";
import { Settings2, Eye, Plus } from "lucide-react";
import { SchemaField } from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";
import { SchemaBuilderDialog } from "@/components/schema-builder/schema-builder-dialog";
import { SchemaViewerDialog } from "@/components/schema-builder/schema-viewer-dialog";
import { generateZodSchema } from "@/utils/schema-utils";

export default function SchemaBuilderPage() {
  const [schemas, setSchemas] = useState<{ id: string; name: string; schema: SchemaField[] }[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<SchemaField | null>(null);

  const handleSaveSchema = (schema: SchemaField[]) => {
    const newSchema = {
      id: Math.random().toString(36).slice(2, 9),
      name: `Schema ${schemas.length + 1}`,
      schema,
    };
    setSchemas((prev) => [...prev, newSchema]);
  };

  const handlePropertySelect = (field: SchemaField) => {
    setSelectedProperty(field);
  };

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto space-y-8">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold">Visual Schema Builder</h1>
          <p className="mb-8 text-xl">Build and manage data schemas visually</p>
        </div>

        <div className="flex justify-center gap-4">
          <SchemaBuilderDialog
            trigger={
              <Button size="lg">
                <Plus className="mr-2 h-5 w-5" />
                Create New Schema
              </Button>
            }
            onSave={handleSaveSchema}
          />
        </div>

        {schemas.length > 0 && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {schemas.map((schemaItem) => (
              <Card key={schemaItem.id} className="transition-shadow hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{schemaItem.name}</span>
                    <div className="flex gap-2">
                      <SchemaBuilderDialog
                        trigger={
                          <Button variant="outline" size="sm">
                            <Settings2 className="h-4 w-4" />
                          </Button>
                        }
                        initialSchema={schemaItem.schema}
                        onSave={(newSchema) => {
                          setSchemas((prev) =>
                            prev.map((s) =>
                              s.id === schemaItem.id ? { ...s, schema: newSchema } : s,
                            ),
                          );
                        }}
                        title={`Edit ${schemaItem.name}`}
                      />
                      <SchemaViewerDialog
                        trigger={
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        }
                        schema={schemaItem.schema}
                        title={`View ${schemaItem.name}`}
                      />
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm">
                      {schemaItem.schema.length} field{schemaItem.schema.length !== 1 ? "s" : ""}
                    </p>
                    <pre className="bg-secondary overflow-x-auto rounded p-2 text-xs">
                      <code>{generateZodSchema(schemaItem.schema).slice(0, 100)}...</code>
                    </pre>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card className="mx-auto max-w-2xl">
          <CardHeader>
            <CardTitle>Property Selection Demo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-primary text-sm">
              This demonstrates using the schema viewer in read-only mode for property selection:
            </p>
            <div className="flex items-center gap-4">
              <SchemaViewerDialog
                trigger={<Button variant="outline">Select Property</Button>}
                schema={schemas[0]?.schema || []}
                title="Select a Property"
                onFieldSelect={handlePropertySelect}
                selectedField={selectedProperty}
              />
              {selectedProperty && (
                <div className="flex items-center gap-2">
                  <span className="text-sm">Selected:</span>
                  <code className="rounded px-2 py-1 text-sm">{selectedProperty.name}</code>
                  <span className="text-xs text-gray-500">({selectedProperty.type})</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
